// Normalización de teléfonos a E.164 sin '+'.
//
// Forma canónica unificada para sistema-focus: dígitos puros, sin '+', sin
// espacios, sin separadores. Ej "56965954796". Esto matchea exactamente con
// el `phone` que el sidecar de WhatsApp guarda en contacts (extraído del JID
// `<phone>@s.whatsapp.net` de Baileys).
//
// Para mostrar al usuario, usar `prettyPhone(p)` que prepende '+'.
//
// IMPORTANTE: este archivo se mantiene en sync con
// `services/whatsapp-qr/src/lib/phone.ts`. Si cambias la lógica acá, cambia
// también allá — ambos archivos definen el contrato del field `contacts.phone`.

const DIGITS_RE = /\d+/g;

/**
 * Normaliza cualquier input a E.164 sin '+'.
 *
 *   "56912345678@s.whatsapp.net"  →  "56912345678"
 *   "+56 9 1234 5678"             →  "56912345678"
 *   "56912345678"                 →  "56912345678"
 *   "tel:+56-9-1234-5678"         →  "56912345678"
 *   ""                            →  null
 *   "abc"                         →  null
 *   "12345" (menos de 8 dígitos)  →  null (probable fragmento)
 */
export function normalizePhone(input: string | null | undefined): string | null {
  if (!input) return null;
  // Si trae @ es un JID — tomamos la parte antes del @.
  const beforeAt = input.includes('@') ? input.split('@')[0]! : input;
  // Concatenamos solo los dígitos.
  const matches = beforeAt.match(DIGITS_RE);
  if (!matches) return null;
  const digits = matches.join('');
  // E.164 válido: 8-15 dígitos. Menos de 8 es probable fragmento (extensión,
  // número doméstico sin código país, etc.).
  if (digits.length < 8 || digits.length > 15) return null;
  return digits;
}

/**
 * Formato de display. Chile móvil: +56 9 1234 5678.
 * Otros números: '+' + dígitos. Vacío → '—'.
 */
export function prettyPhone(phone: string | null | undefined): string {
  const normalized = normalizePhone(phone);
  if (!normalized) return '—';
  if (normalized.startsWith('56') && normalized.length === 11 && normalized[2] === '9') {
    return `+56 9 ${normalized.slice(3, 7)} ${normalized.slice(7)}`;
  }
  if (normalized.startsWith('56') && normalized.length >= 10) {
    const rest = normalized.slice(2);
    return `+56 ${rest}`;
  }
  return `+${normalized}`;
}

/**
 * Útil para tel: links — no agrega espacios, solo el '+'.
 */
export function phoneToTelLink(phone: string | null | undefined): string | null {
  const normalized = normalizePhone(phone);
  if (!normalized) return null;
  return `tel:+${normalized}`;
}

/**
 * Útil para wa.me / WhatsApp deep-links (sin '+').
 */
export function phoneToWhatsAppLink(phone: string | null | undefined): string | null {
  const normalized = normalizePhone(phone);
  if (!normalized) return null;
  return `https://wa.me/${normalized}`;
}
